<template>
  <div
    v-if="visible"
    class="overlay"
    role="dialog"
    aria-modal="true"
    @keydown.esc="emitClose"
  >
    <div class="dialog">
      <header class="dialog-header">
        <h2 class="title">
          Abwesenheitsdetails – {{ studentName }} – <span class="muted meta mt-0 mb-3">
            Zeitraum: {{ formatDate(from) }} – {{ formatDate(to) }}
          </span>
        </h2>
        <button
          class="btn"
          type="button"
          @click="emitClose"
        >
          Schließen
        </button>
      </header>

      <section class="dialog-body">
        <div
          class="summary-box"
          role="region"
          aria-label="Zusammenfassung"
        >
          <div class="summary-list">
            <div class="summary-row summary-header">
              <div
                class="summary-cell"
                style="border-top-left-radius: 8px;"
              >
                <div class="">
                  <span class="font-bold">Fehltage</span>
                </div>
              </div>
              <div class="summary-cell">
                <div class="font-bold">
                  unentschuldigt
                </div>
              </div>
              <div class="summary-cell">
                <div style="line-height: 14px;">
                  Fehlzeit <span
                    class="muted d-block"
                    style="font-size: 10px;"
                  >(gesamt / unentschuldigt)</span>
                </div>
              </div>
              <div class="summary-cell">
                <div class="">
                  Krankmeldungen
                </div>
              </div>
              <div class="summary-cell">
                <div style="line-height: 14px;">
                  Freimeldungen <span
                    class="muted"
                    style="font-size: 10px;"
                  >(Beurlaubung/Familie)</span>
                </div>
              </div>
              <div
                class="summary-cell"
                style="border-top-right-radius: 8px;"
              >
                <div class="">
                  Verspätungsmeldungen
                </div>
              </div>
            </div>

            <div class="summary-row summary-body">
              <div
                class="summary-cell"
                style="border-bottom-left-radius: 8px;"
              >
                <div class="font-bold">
                  {{ formatDays(summary.daysReal) }} Tage
                </div>
              </div>
              <div class="summary-cell">
                <div class="font-bold">
                  {{ formatDays(summary.daysUnexcusedTotal) }} Tage
                </div>
              </div>
              <div class="summary-cell">
                <div>
                  {{ formatHoursToHHMM(summary.hoursAbsence) }} h
                  ({{ formatCount(summary.absencesCount) }})
                  /
                  {{ formatHoursToHHMM(summary.hoursAbsenceUnexcused) }} h
                  ({{ formatCount(summary.absencesUnexcusedCount) }})
                </div>
              </div>
              <div class="summary-cell">
                <div class="">
                  {{ formatDays(summary.daysExcusedIllness) }} Tage
                </div>
              </div>
              <div class="summary-cell">
                <div class="">
                  {{ formatDays(summary.daysExcusedOther) }} Tage
                </div>
              </div>
              <div
                class="summary-cell"
                style="border-bottom-right-radius: 8px;"
              >
                <div class="">
                  {{ formatDays(summary.daysTardiness) }} Tage
                </div>
              </div>
            </div>
          </div>
        </div>

        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>

        <p
          v-else-if="loading"
          class="mt-12"
        >
          Lädt …
        </p>

        <p
          v-else-if="items.length === 0"
          class="notice mt-12"
          role="status"
        >
          Keine Einträge für diesen Zeitraum.
        </p>

        <template v-else>
          <!-- Filterleiste + Zusammenfassung -->
          <div
            class="body-top mt-0 mb-0"
            aria-label="Filter und Zusammenfassung"
          >
            <div
              class="filters"
              role="group"
              aria-label="Filter: Anzeigen"
            >
              <!-- Gruppe: Art -->
              <div
                class="btn-group"
                role="group"
                aria-label="Filter: Art"
              >
                <button
                  type="button"
                  class="btn"
                  :class="{'btn-primary': filterType === 'all'}"
                  title="Alle Einträge"
                  @click="filterType = 'all'"
                >
                  Alle
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="{'btn-primary': filterType === 'absence'}"
                  title="Fehlzeiten (teilweise/volle Abwesenheit)"
                  @click="filterType = 'absence'"
                >
                  Fehlzeiten
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="{'btn-primary': filterType === 'absence-day'}"
                  title="Fehltage (kein Check-in am Tag)"
                  @click="filterType = 'absence-day'"
                >
                  Fehltage
                </button>
              </div>

              <!-- Gruppe: Entschuldigung (implizit wirkend) -->
              <div
                class="btn-group"
                role="group"
                aria-label="Filter: Entschuldigung"
              >
                <button
                  type="button"
                  class="btn"
                  :class="{'btn-primary': filterExcuse === 'excused'}"
                  title="Entschuldigte Abwesenheiten"
                  @click="filterExcuse = (filterExcuse === 'excused' ? 'any' : 'excused')"
                >
                  <i
                    class="pi pi-check-circle"
                    aria-hidden="true"
                  />
                  entschuldigt
                </button>
                <button
                  type="button"
                  class="btn"
                  :class="{'btn-primary': filterExcuse === 'unexcused'}"
                  title="Unentschuldigte Abwesenheiten"
                  @click="filterExcuse = (filterExcuse === 'unexcused' ? 'any' : 'unexcused')"
                >
                  <i
                    class="pi pi-times-circle"
                    aria-hidden="true"
                  />
                  unentschuldigt
                </button>
              </div>

              <!-- Aktionen: Reset + PDF-Export -->
              <div
                class="filters-actions"
                role="group"
                aria-label="Aktionen"
              >
                <button
                  type="button"
                  class="btn"
                  :disabled="loading"
                  title="Filter auf Standard zurücksetzen"
                  @click="resetFilters"
                >
                  Reset
                </button>
                <button
                  type="button"
                  class="btn ml-4"
                  :disabled="loading || filteredDayGroups.length === 0"
                  style="background-color: lightgoldenrodyellow"
                  title="Tabellenergebnisse als PDF exportieren"
                  @click="exportPdf"
                >
                  <i
                    class="pi pi-file-pdf"
                    aria-hidden="true"
                  />
                  PDF
                </button>
              </div>
            </div>

            <!-- Zusammenfassung oben rechts -->
          </div>

          <!-- Tabelle (pro Tag eine Zeile) -->
          <div class="table mt-12">
            <div class="thead">
              <div>Datum</div>
              <div>Angebotsname</div>
              <div>Zeiträume</div>
              <div class="text-center">
                Fehlzeit
              </div>
              <div class="text-center">
                Ankommen / Entlassen
              </div>
            </div>
            <div class="tbody">
              <div
                v-for="day in pagedDayGroups"
                :key="day.date"
                class="row"
                :class="rowBackgroundClass(day)"
              >
                <div
                  class="cell"
                  :class="{ 'cell-bold': day.type === 'absence-day' }"
                >
                  {{ formatDate(day.date) }}
                </div>

                <!-- Angebote-Namen als Liste -->
                <div class="cell multi">
                  <div class="list">
                    <div
                      v-for="(o, i) in day.offers"
                      :key="i"
                    >
                      {{ o.subject || '—' }}
                    </div>
                  </div>
                </div>

                <!-- Angebots-Zeiträume als Liste -->
                <div class="cell multi">
                  <div class="list">
                    <div
                      v-for="(o, i) in day.offers"
                      :key="i"
                    >
                      {{ formatTimeRange(o.offerStart, o.offerEnd) }}
                    </div>
                  </div>
                </div>

                <!-- Fehlzeit je Angebot -->
                <div class="cell multi text-center">
                  <div class="list">
                    <div
                        v-for="(o, i) in day.offers"
                        :key="i"
                        :class="[
                        hasOfferAbsence(o) && day.type !== 'absence-day' ? 'absence-split' : '',
                        { 'line-bold': hasOfferAbsence(o) && day.type !== 'absence-day' }
                      ]"
                    >
                      <template v-if="hasOfferAbsence(o)">
                        <div
                            v-if="getExcusedSecondsEffective(o) > 0"
                            class="absence-excused absence-line"
                        >
                          {{ formatDuration(getExcusedSecondsEffective(o)) }}
                        </div>
                        <div
                            v-if="getUnexcusedSecondsEffective(o) > 0"
                            class="absence-unexcused absence-line"
                        >
                          {{ formatDuration(getUnexcusedSecondsEffective(o)) }}
                        </div>
                      </template>
                      <template v-else>
                        —
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Ankommen / Entlassen: anzeigen, wenn vorhanden -->
                <div class="cell multi text-center">
                  <div class="list">
                    <div class="cell">
                      {{ formatTimeRange(day.arrivedAt, day.releasedAt) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination (analog zu AbsenceDaysCard.vue, aber dialog-lokal) -->
          <div
            class="mt-12 pagination"
          >
            <span class="muted pagination-info">
              Tage gesamt: {{ totalDays }}
            </span>

            <label
              class="muted"
              for="absence-details-page-size"
            >
              pro Seite:
            </label>
            <select
              id="absence-details-page-size"
              v-model.number="pageSize"
              :disabled="loading"
            >
              <option
                v-for="n in [5,10,20,50]"
                :key="n"
                :value="n"
              >
                {{ n }}
              </option>
            </select>

            <button
              class="btn"
              :disabled="page <= 1 || loading"
              @click="goPrev"
            >
              Zurück
            </button>

            <span class="muted">
              Seite {{ page }} / {{ lastPage }}
            </span>

            <button
              class="btn"
              :disabled="page >= lastPage || loading"
              @click="goNext"
            >
              Weiter
            </button>
          </div>
        </template>
      </section>

      <footer class="dialog-footer">
        <button
          class="btn btn-primary"
          type="button"
          @click="emitClose"
        >
          OK
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import {onMounted, ref, watch, computed} from 'vue';
import api from '@/services/api';
import {jsPDF} from 'jspdf';
import autoTable from 'jspdf-autotable';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  studentId: {
    type: [Number, String],
    required: true
  },
  studentName: {
    type: String,
    required: true
  },
  from: {
    type: String,
    required: true
  },
  to: {
    type: String,
    required: true
  },
  schoolYearCode: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['close']);

const loading = ref(false);
const error = ref('');
const items = ref([]);

// Filtergruppen
const filterType = ref('all'); // 'all' | 'absence' | 'absence-day'
const filterExcuse = ref('any'); // 'any' | 'excused' | 'unexcused'

// Aggregierte Chart-Daten für Zusammenfassung
const chartItems = ref([]);

/**
 * Aggregierte Kennzahlen für die Zusammenfassung
 */
const summary = computed(() => {
  const totals = {
    daysReal: 0,
    hoursAbsence: 0,
    hoursAbsenceUnexcused: 0,
    daysUnexcusedTotal: 0,
    daysExcusedIllness: 0,
    daysExcusedOther: 0,
    daysTardiness: 0, // derzeit nicht getrennt verfügbar
    absencesCount: 0,
    absencesUnexcusedCount: 0
  };

  let hasChartUnexcusedHours = false;

  // Stunden + Tage aus der Chart-API (falls vorhanden)
  for (const p of chartItems.value) {
    const d = p.days || {};
    totals.daysReal += Number(d.totalReal || 0);
    totals.daysUnexcusedTotal += Number(d.unexcusedReal || 0);
    totals.daysExcusedIllness += (Number(d.excusedIllnessAccidentReal || 0) + Number(d.excusedIllnessAccidentComputed || 0));
    totals.daysExcusedOther += (Number(d.excusedOtherReal || 0) + Number(d.excusedOtherComputed || 0));

    totals.hoursAbsence += Number(p.absenceHoursTotal || 0);

    const unexcusedFromChart = Number(p?.hours?.unexcused);
    if (!Number.isNaN(unexcusedFromChart)) {
      hasChartUnexcusedHours = true;
      totals.hoursAbsenceUnexcused += unexcusedFromChart;
    }
  }

  // Fallback: falls die Chart-API keine unentschuldigten Stunden liefert,
  // aus Detaildaten (Sekunden) approximieren.
  if (!hasChartUnexcusedHours) {
    let unexcusedSeconds = 0;
    for (const r of items.value) {
      const absence = Number(r?.absenceSeconds || 0);
      if (absence <= 0) continue;
      const excused = Math.max(0, Number(r?.excusedSeconds || 0));
      unexcusedSeconds += Math.max(0, absence - excused);
    }
    totals.hoursAbsenceUnexcused = unexcusedSeconds / 3600;
  }

  // Anzahl Fehlzeiten (Detaildaten: pro Angebot/Datensatz)
  for (const r of items.value) {
    const absence = Number(r?.absenceSeconds || 0);
    if (absence <= 0) continue;

    totals.absencesCount += 1;

    const excused = Math.max(0, Number(r?.excusedSeconds || 0));
    const tolerance = 1;
    const isExcused = Boolean(r?.isExcusedAbsence) || excused >= Math.max(0, absence - tolerance);
    if (!isExcused) {
      totals.absencesUnexcusedCount += 1;
    }
  }

  return totals;
});

function formatDays(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('de-DE', {maximumFractionDigits: 2});
}

function formatCount(value) {
  const n = Math.max(0, Math.round(Number(value) || 0));
  return n.toLocaleString('de-DE');
}

function formatHoursToHHMM(value) {
  const v = Math.max(0, Number(value) || 0);
  const h = Math.floor(v);
  const m = Math.round((v - h) * 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

async function loadSummary() {
  try {
    const params = {
      studentId: props.studentId,
      from: props.from || undefined,
      to: props.to || undefined,
      schoolYearCode: props.schoolYearCode || undefined,
      groupBy: 'week'
    };
    const {data} = await api.get('/reports/absence-days/chart', {params});
    const list = Array.isArray(data) ? data : [];
    chartItems.value = list.map((p) => ({
      days: {
        totalReal: Number(p?.days?.totalReal ?? 0),
        unexcusedReal: Number(p?.days?.unexcusedReal ?? 0),
        unexcusedComputed: Number(p?.days?.unexcusedComputed ?? 0),
        excusedIllnessAccidentReal: Number(p?.days?.excusedIllnessAccidentReal ?? 0),
        excusedIllnessAccidentComputed: Number(p?.days?.excusedIllnessAccidentComputed ?? 0),
        excusedOtherReal: Number(p?.days?.excusedOtherReal ?? 0),
        excusedOtherComputed: Number(p?.days?.excusedOtherComputed ?? 0)
      },
      hours: {
        unexcused: Number(p?.hours?.unexcused ?? 0),
        excused: Number(p?.hours?.excused ?? 0)
      },
      absenceHoursTotal: Number(p?.absenceHoursTotal ?? 0)
    }));
  } catch (e) {
    chartItems.value = [];
    // Zusammenfassung fällt still auf 0 zurück
  }
}

// Pagination (clientseitig, analog zur Karte)
const page = ref(1);
const pageSize = ref(10);

// Gruppierung: eine Zeile pro Tag mit Angebotsliste
const dayGroups = computed(() => {
  const byDay = new Map();
  for (const r of items.value) {
    const day = r.date || r.day || '';
    if (!day) continue;
    if (!byDay.has(day)) {
      byDay.set(day, {
        date: day,
        offers: [],
        totalOfferSeconds: 0,
        totalAttendanceSeconds: 0,
        absenceSeconds: 0,
        hasCheckin: false,
        arrivedAt: r.arrivedAt ?? null,
        releasedAt: r.releasedAt ?? null,
        type: 'present',
        isExcusedDay: Boolean(r.isExcusedDay || r.hasExcusedNoticeDay)
      });
    }
    const g = byDay.get(day);

    g.offers.push({
      subject: r.subject ?? null,
      offerStart: r.offerStart ?? null,
      offerEnd: r.offerEnd ?? null,
      offerSeconds: Number(r.offerSeconds || 0),
      attendanceSeconds: Number(r.attendanceSeconds || 0),
      checkinAt: r.checkinAt ?? null,
      checkoutAt: r.checkoutAt ?? null,
      absenceSeconds: Number(r.absenceSeconds || 0),
      excusedSeconds: Number(r.excusedSeconds || 0),
      isExcusedAbsence: Boolean(r.isExcusedAbsence)
    });
    g.totalOfferSeconds += Number(r.offerSeconds || 0);
    g.totalAttendanceSeconds += Number(r.attendanceSeconds || 0);
    g.absenceSeconds = Math.max(
        Number(g.absenceSeconds || 0),
        Number(r.absenceSeconds || 0),
        Math.max(0, Number(r.offerSeconds || 0) - Number(r.attendanceSeconds || 0)) // Fallback
    );
    g.hasCheckin = g.hasCheckin || Boolean(r.hasCheckin || (r.checkinAt && r.checkoutAt));

    // Falls mehrere Stays am Tag existieren, immer frühestes Ankommen / spätestes Entlassen nehmen
    if (!g.arrivedAt || (r.arrivedAt && parseDbTimestampToUtcDate(r.arrivedAt) < parseDbTimestampToUtcDate(g.arrivedAt))) {
      g.arrivedAt = r.arrivedAt ?? g.arrivedAt;
    }
    if (!g.releasedAt || (r.releasedAt && parseDbTimestampToUtcDate(r.releasedAt) > parseDbTimestampToUtcDate(g.releasedAt))) {
      g.releasedAt = r.releasedAt ?? g.releasedAt;
    }
  }

  // Tages-Typ bestimmen
  const out = Array.from(byDay.values()).sort((a, b) => {
    if (a.date === b.date) return 0;
    // Jüngste (größere) Daten zuerst
    return a.date < b.date ? 1 : -1;
  });
  for (const g of out) {
    const offer = Number(g.totalOfferSeconds || 0);
    const att = Number(g.totalAttendanceSeconds || 0);
    if (!g.hasCheckin && !g.arrivedAt && offer > 0) {
      g.type = 'absence-day';
    } else if (att <= 0 && offer > 0) {
      g.type = 'absence';
    } else if (att > 0 && att < offer) {
      g.type = 'absence-partial';
    } else {
      g.type = 'present';
    }
  }

  return out;
});

const filteredDayGroups = computed(() => {
  // Zuerst nach Art filtern
  let list = dayGroups.value;
  switch (filterType.value) {
    case 'absence':
      list = list.filter(d => d.type === 'absence' || d.type === 'absence-partial');
      break;
    case 'absence-day':
      list = list.filter(d => d.type === 'absence-day');
      break;
    default:
      // 'all' → keine Einschränkung
      break;
  }

  // Optional nach Entschuldigung filtern (implizit)
  if (filterExcuse.value === 'excused') {
    list = list.filter(d => {
      if (d.type === 'absence-day') {
        return Boolean(d.isExcusedDay);
      }
      if (d.type === 'absence' || d.type === 'absence-partial') {
        return d.offers.some(o => hasOfferAbsence(o) && isOfferExcused(o));
      }
      return false;
    });
  } else if (filterExcuse.value === 'unexcused') {
    list = list.filter(d => {
      if (d.type === 'absence-day') {
        return !(d.isExcusedDay);
      }
      if (d.type === 'absence' || d.type === 'absence-partial') {
        return d.offers.some(o => hasOfferAbsence(o) && !isOfferExcused(o));
      }
      return false;
    });
  }

  return list;
});

const totalDays = computed(() => filteredDayGroups.value.length);
const lastPage = computed(() =>
    Math.max(1, Math.ceil(totalDays.value / pageSize.value))
);
const pagedDayGroups = computed(() => {
  const start = (page.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredDayGroups.value.slice(start, end);
});

function goPrev() {
  if (page.value > 1) {
    page.value -= 1;
  }
}

function goNext() {
  if (page.value < lastPage.value) {
    page.value += 1;
  }
}

watch(pageSize, () => {
  page.value = 1;
});

watch([filterType, filterExcuse], () => {
  page.value = 1;
});

function resetFilters() {
  filterType.value = 'all';
  filterExcuse.value = 'any';
  page.value = 1;
}

async function exportPdf() {
  try {
    const doc = new jsPDF({unit: 'pt', format: 'a4', orientation: 'portrait'});
    const ml = 40;
    let y = 40;

    // Header
    doc.setFontSize(14);
    doc.text(`Abwesenheitsdetails – ${props.studentName}`, ml, y);
    doc.setFontSize(9);
    doc.setTextColor(100);
    y += 18;
    doc.text(`Zeitraum: ${formatDate(props.from)} – ${formatDate(props.to)}`, ml, y);
    y += 8;

    // Zusammenfassung (als Tabelle mit Rahmen wie im HTML)
    const sum = summary.value || {};
    const summaryHead = [[
      'Fehltage (kumm.)',
      'unentschuldigt',
      'Fehlzeit (gesamt / unentsch.)',
      'Krankmeldungen',
      'Freimeldungen',
      'Verspätungsmeldungen'
    ]];
    const summaryRow = [[
      `${formatDays(sum.daysReal || 0)} Tage`,
      `${formatDays(sum.daysUnexcusedTotal || 0)} Tage`,
      `${formatHoursToHHMM(sum.hoursAbsence || 0)} h / ${formatHoursToHHMM(sum.hoursAbsenceUnexcused || 0)} h`,
      `${formatDays(sum.daysExcusedIllness || 0)} Tage`,
      `${formatDays(sum.daysExcusedOther || 0)} Tage`,
      `${formatDays(sum.daysTardiness || 0)} Tage`
    ]];

    autoTable(doc, {
      head: summaryHead,
      body: summaryRow,
      startY: y + 8,
      styles: {fontSize: 9, cellPadding: 4, halign: 'center'},
      headStyles: {fillColor: [204, 204, 204], textColor: 255, fontSize: 9, fontStyle: 'normal'},
      theme: 'grid',
      margin: {left: ml, right: 40}
    });

    y = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : y) + 30;

    const filterLabelMap = {all: 'Alle', absence: 'Fehlzeiten', 'absence-day': 'Fehltage'};
    const excuseLabelMap = {any: 'alle', excused: 'entschuldigt', unexcused: 'unentschuldigt'};
    doc.text(`Filter: ${filterLabelMap[filterType.value]} | ${excuseLabelMap[filterExcuse.value]}`, ml, y);
    doc.setTextColor(0);

    // Tabelle
    const head = [['Datum', 'Angebot', 'Zeiträume', 'Fehlzeit', 'Anwesenheit']];

    // Flache Zeilen + Metadaten für bedingte Styles
    const rows = [];
    const rowMeta = [];

    for (const day of filteredDayGroups.value) {
      const offers = Array.isArray(day.offers) ? day.offers : [];

      if (day.type === 'absence-day') {
        // Ein Zeile je Fehltag; Angebotsnamen kommagetrennt, Zeiträume/Fehlzeit sind "—"
        rows.push([
          formatDate(day.date),
          offers.map(o => o.subject || '—').join(', ') || '—',
          '—',
          '—',
          formatTimeRange(day.arrivedAt, day.releasedAt) || '—'
        ]);
        rowMeta.push({
          type: 'absence-day',
          unexcusedDay: !day.isExcusedDay
        });
      } else {
        // Pro Angebot eine Zeile (ermöglicht gezieltes Einfärben der Fehlzeit-Zelle)
        offers.forEach((o, idx) => {
          const hasAbs = hasOfferAbsence(o);
          const isExc = hasAbs && isOfferExcused(o);
          rows.push([
            idx === 0 ? formatDate(day.date) : '', // Datum nur in der ersten Zeile des Tages
            o.subject || '—',
            formatTimeRange(o.offerStart, o.offerEnd) || '—',
            hasAbs ? formatDuration(Number(o.absenceSeconds)) : '—',
            idx === 0 ? (formatTimeRange(day.arrivedAt, day.releasedAt) || '—') : ''
          ]);
          rowMeta.push({
            type: 'offer',
            unexcusedOffer: hasAbs && !isExc
          });
        });
        // Falls keine Angebote vorhanden sind, dennoch eine neutrale Zeile ausgeben
        if (offers.length === 0) {
          rows.push([
            formatDate(day.date),
            '—',
            '—',
            '—',
            formatTimeRange(day.arrivedAt, day.releasedAt) || '—'
          ]);
          rowMeta.push({
            type: 'empty',
            unexcusedOffer: false
          });
        }
      }
    }

    autoTable(doc, {
      head,
      body: rows,
      startY: y + 8,
      styles: {fontSize: 10, cellPadding: 4, overflow: 'linebreak'},
      headStyles: {fillColor: [204, 204, 204], textColor: 255, fontStyle: 'normal'},
      theme: 'striped',
      columnStyles: {
        0: {cellWidth: 70},
        1: {cellWidth: 'auto'},
        2: {cellWidth: 80},
        3: {cellWidth: 60},
        4: {cellWidth: 80}
      },
      margin: {left: ml, right: 40},
      didParseCell: (data) => {
        // Nur Body-Zellen (nicht Kopf) einfärben
        if (data.section !== 'body') return;
        const meta = rowMeta[data.row.index] || {};

        // RGB für "danger" (#EF4444 → 239,68,68)
        const danger = [239, 68, 68];

        // Unentschuldigter Fehltag: Datum (Spalte 0) rot hinterlegen
        if (meta.type === 'absence-day' && meta.unexcusedDay && data.column.index === 0) {
          data.cell.styles.fillColor = danger;
          data.cell.styles.textColor = 255; // weißer Text für Kontrast
        }

        // Unentschuldigte Fehlzeit (pro Angebot): Fehlzeit-Spalte (Index 3) rot hinterlegen
        if (meta.type === 'offer' && meta.unexcusedOffer && data.column.index === 3) {
          data.cell.styles.fillColor = danger;
          data.cell.styles.textColor = 255; // weißer Text
        }
      }
    });

    const fileSafe = (s) => String(s ?? '')
        .normalize('NFKD')
        .replace(/[^\w\s.-]/g, '')
        .trim()
        .replace(/\s+/g, '_');

    const fileName = `Abwesenheiten_${fileSafe(props.studentName)}_${props.from}_${props.to}.pdf`;
    doc.save(fileName);
  } catch (e) {
    console.error('PDF-Export fehlgeschlagen:', e);
  }
}

function rowBackgroundClass(day) {
  if (day.type !== 'absence-day') {
    return '';
  }
  return day.isExcusedDay ? 'row-excused' : 'row-unexcused';
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

function parseDbTimestampToUtcDate(v) {
  if (!v) return null;

  let iso = String(v).trim();

  // Leerzeichen zwischen Datum/Zeit in "T" wandeln
  iso = iso.replace(' ', 'T');

  // Millisekunden ggf. auf 3 Stellen kürzen
  iso = iso.replace(/(\.\d{3})\d+$/, '$1');

  // Zeitzonen-Offset wie "+00" → "+00:00" normalisieren
  iso = iso.replace(/([+-]\d{2})$/, '$1:00');

  // Falls noch kein Z/Offset dran ist, als UTC interpretieren
  if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(iso)) {
    iso += 'Z';
  }

  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatTime(v) {
  const dt = parseDbTimestampToUtcDate(v);
  if (!dt) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(dt);
}

function formatTimeRange(a, b) {
  if (!a || !b) return '—';
  return `${formatTime(a)}–${formatTime(b)}`;
}

function formatDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

function hasOfferAbsence(o) {
  const absence = Number(o?.absenceSeconds) || 0;
  return absence > 0;
}

function getExcusedSecondsEffective(o) {
  const absence = Math.max(0, Number(o?.absenceSeconds || 0));
  const excused = Math.max(0, Number(o?.excusedSeconds || 0));
  return Math.min(absence, excused);
}

function getUnexcusedSecondsEffective(o) {
  const absence = Math.max(0, Number(o?.absenceSeconds || 0));
  const excusedEff = getExcusedSecondsEffective(o);
  return Math.max(0, absence - excusedEff);
}

function isOfferExcused(o) {
  if (!hasOfferAbsence(o)) return false;
  if (o.isExcusedAbsence) return true;

  const absence = Number(o.absenceSeconds || 0);
  const excused = Number(o.excusedSeconds || 0);
  const tolerance = 1; // 1 Sekunde Toleranz
  return excused >= Math.max(0, absence - tolerance);
}

async function load() {
  if (!props.visible) return;

  loading.value = true;
  error.value = '';
  items.value = [];
  page.value = 1;

  try {
    const params = {
      studentId: props.studentId,
      from: props.from || undefined,
      to: props.to || undefined,
      schoolYearCode: props.schoolYearCode || undefined
    };

    const {data} = await api.get('/reports/absence-days/daily', {params});

    const list = Array.isArray(data) ? data : (data.items ?? []);
    items.value = list.map((r, idx) => ({
      id: r.id ?? r.offerId ?? idx,
      date: r.date ?? r.day ?? '',
      subject: r.subject ?? null,
      offerStart: r.offerStart ?? r.starts_at ?? null,
      offerEnd: r.offerEnd ?? r.ends_at ?? null,
      offerSeconds: Number(r.offerSeconds ?? r.offer_seconds ?? 0),
      attendanceSeconds: Number(r.attendanceSeconds ?? r.attendance_seconds ?? 0),
      absenceSeconds: Number(r.absenceSeconds ?? r.absence_seconds ?? 0),
      excusedSeconds: Number(r.excusedSeconds ?? r.excused_seconds ?? 0),
      checkinAt: r.checkinAt ?? r.checkin_at ?? null,
      checkoutAt: r.checkoutAt ?? r.checkout_at ?? null,
      hasCheckin: r.hasCheckin ?? r.has_checkin ?? null,
      arrivedAt: r.arrivedAt ?? r.arrived_at ?? null,
      releasedAt: r.releasedAt ?? r.released_at ?? null,
      hasExcusedNoticeDay: r.hasExcusedNoticeDay ?? r.has_excused_notice_day ?? false,
      isExcusedDay: r.isExcusedDay ?? r.is_excused_day ?? false,
      isExcusedAbsence: r.isExcusedAbsence ?? r.is_excused_absence ?? false,
      type: r.type ?? null
    }));
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Details konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

function emitClose() {
  emit('close');
}

// neu laden, wenn geöffnet ODER Filter/Schüler gewechselt
watch(
    () => [props.visible, props.studentId, props.from, props.to, props.schoolYearCode],
    ([visible]) => {
      if (visible) {
        // Filter & Pagination beim Öffnen zurücksetzen
        filterType.value = 'all';
        filterExcuse.value = 'any';
        page.value = 1;

        load();
        loadSummary();
      }
    },
    {immediate: true}
);

onMounted(() => {
  if (props.visible) {
    load();
    loadSummary();
  }
});
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog {
  width: min(100%, 960px);
  background: var(--surface);
  border-radius: 10px;
  border: 1px solid var(--border);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .15);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.dialog-header,
.dialog-footer {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dialog-footer {
  border-top: 1px solid var(--border);
  border-bottom: none;
  justify-content: flex-end;
}

.dialog-body {
  padding: 12px;
  /* bisheriges Scrollen hier abschalten und stattdessen innerlich layouten */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.meta {
  font-size: 12px;
}

.mt-12 {
  margin-top: 12px;
}

.mb-3 {
  margin-bottom: 12px;
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 12px;
}

/* Filterleiste */
.filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 0;
  margin-top: 10px;
}

.filters-actions {
  margin-left: auto;
  display: inline-flex;
  gap: 8px;
  align-items: center;
}

/* Top-Bereich in der Dialog-Body: Filter links, Zusammenfassung rechts */
.body-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

/* Zusammenfassung rechts der Filterleiste */
.summary-box {
  margin-left: auto;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 8px;
  width: 100%;
  font-size: 12px;
  margin-bottom: 10px;
}

.summary-title {
  margin: 0 0 6px 0;
}

.summary-list {
  display: grid;
  grid-template-columns: 1fr;
}

.summary-row {
  display: grid;
  grid-template-columns: 14% 14% 17% 17% 18% 20%;
}

.summary-row.summary-body {
  text-align: center;
}

.summary-row.summary-header {
  font-weight: 500;
  text-align: center;
}

.summary-cell {
  display: flex;
  border-left: 1px solid var(--border);
  padding: 6px 8px;
  background: var(--surface);
  align-items: center;
  justify-content: center;

}

.summary-cell:first-child {
  border-left: 0;

}

.summary-row.summary-header .summary-cell {
  background: var(--bg);
  border-color: var(--border-strong);
}


/* Tabelle */
.table {
  width: 100%;
  /* neu: Tabelle füllt den verfügbaren Raum in der Dialog-Body */
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.thead,
.row {
  display: grid;
  grid-template-columns: 110px auto 130px 70px 180px;
  gap: 8px;
  align-items: start;
}

.thead {
  font-size: 12px;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
  padding-bottom: 4px;
}

.thead div {
  padding: 0 3px;
}

/* neu: nur die tbody vertikal scrollen */
.tbody {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.tbody .row {
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.cell {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 3px;
}

.cell.multi {
  white-space: normal;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.list .line-bold {
  font-weight: 600;
}

.row-bold {
  font-weight: 600;
}

.cell-bold {
  font-weight: 600;
}

/* Entschuldigt / unentschuldigt Hintergründe */
.row-excused {
  background: color-mix(in srgb, var(--success) 8%, #fff 92%);
}

.row-unexcused {
  background: color-mix(in srgb, var(--danger) 22%, #fff 78%);
}

.absence-excused {
  background: color-mix(in srgb, var(--success) 12%, #fff 88%);
}

.absence-unexcused {
  background: color-mix(in srgb, var(--danger) 22%, #fff 78%);
}

.absence-line {
  padding: 0 4px;
  border-radius: 6px;
  display: inline-block;
}

.absence-split {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}

.pagination-info {
  margin-right: auto;
}

/* Buttons / Badges wie Styleguide */
.btn {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
}

/* Button-Gruppen für haptische Bedienung */
.btn-group {
  display: inline-flex;
  vertical-align: middle;
}

.btn-group .btn {
  border-radius: 0;
  position: relative;
  z-index: 1;
}

.btn-group .btn + .btn {
  margin-left: -1px; /* gemeinsame Kante ohne Doppelrand */
}

.btn-group .btn:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.btn-group .btn:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.notice {
  border-radius: 6px;
  border: 1px solid var(--border);
  padding: 10px 12px;
  background: var(--surface);
}

.notice-danger {
  background: color-mix(in srgb, var(--danger) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
}

.badge.success {
  background: color-mix(in srgb, var(--success) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--success) 40%, #fff 60%);
}

.badge.danger {
  background: color-mix(in srgb, var(--danger) 10%, #fff 90%);
  border-color: color-mix(in srgb, var(--danger) 40%, #fff 60%);
}
</style>